import { useState, useCallback, useRef, useEffect } from "react";

const initialState = {
  data: null,
  error: null,
  isIdle: true,
  isLoading: false,
  isSuccess: false,
  isError: false
};

function useMutation(mutationFn, options = {}) {
  const { onMutate, onSuccess, onError, onSettled } = options;

  const [state, setState] = useState(initialState);
  const mutationIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const mutate = useCallback(
    async (variables) => {
      const currentMutationId = ++mutationIdRef.current;

      let context;
      try {
        context = await onMutate?.(variables);
      } catch (err) {
        // Decide how you want to handle this; here we fail the mutation
        if (isMountedRef.current && currentMutationId === mutationIdRef.current) {
          setState({
            ...initialState,
            isIdle: false,
            isError: true,
            error: err
          });
        }
        onError?.(err, variables, undefined);
        onSettled?.(null, err, variables, undefined);
        throw err;
      }

      if (isMountedRef.current && currentMutationId === mutationIdRef.current) {
        setState({
          ...initialState,
          isIdle: false,
          isLoading: true
        });
      }

      try {
        const result = await mutationFn(variables);

        if (isMountedRef.current && currentMutationId === mutationIdRef.current) {
          setState({
            data: result,
            error: null,
            isIdle: false,
            isLoading: false,
            isSuccess: true,
            isError: false
          });
          onSuccess?.(result, variables, context);
          onSettled?.(result, null, variables, context);
        }

        return result;
      } catch (err) {
        if (isMountedRef.current && currentMutationId === mutationIdRef.current) {
          setState({
            data: null,
            error: err,
            isIdle: false,
            isLoading: false,
            isSuccess: false,
            isError: true
          });
          onError?.(err, variables, context);
          onSettled?.(null, err, variables, context);
        }

        throw err;
      }
    },
    [mutationFn, onMutate, onSuccess, onError, onSettled]
  );

  const reset = useCallback(() => {
    setState(initialState);
    mutationIdRef.current = 0;
  }, []);

  return {
    ...state,
    mutate,
    reset
  };
}

export default useMutation;
